import React, { useState, useRef, useEffect, useMemo } from 'react';
import { FixedSizeList as List } from 'react-window';
import { FunnelIcon } from '@heroicons/react/24/solid';
import DetailedTransactionCard from './DetailedTransactionCard';
import SpinnerLoad from 'views/finance/moduleComponents/SpinnerLoad';
import DropdownCustomDark from 'views/finance/moduleComponents/DropdownCustomDark';
import DatePicker from 'shared_items/DatePicker';
import { useFinanceProfile } from 'views/finance/moduleServices/finance_profilecontext';

const HEADER_MAIN_PX = 64;
/** Expanded filter strip (dates + other filters, may wrap one line). */
const HEADER_FILTER_ROW_PX = 112;
/** Fixed strip below filters (matches py-2 + text-sm line ~36–40px) */
const TOTAL_ROW_PX = 40;

/** Same numeric format as DetailedTransactionCard (`flow_value` display). */
const formatFlowValueLikeRow = (value) => {
  const numValue = parseFloat(value);
  return numValue.toFixed(2);
};

const DetailedTransactionList = ({
  flows,
  height = 600,
  itemSize = 100,
  loading = false,
  error = null,
  selectedType,
  setSelectedType,
  selectedCategory,
  setSelectedCategory,
  selectedAccount = '',
  setSelectedAccount,
  flowSearchText = '',
  onFlowSearchTextChange,
  selectedCategL1 = '',
  setSelectedCategL1,
  selectedCategL2 = '',
  setSelectedCategL2,
  categories = [],
  categoriesL1 = [],
  categoriesL2 = [],
  accounts = [],
  flowStartDate = '',
  flowEndDate = '',
  onFlowStartDateChange,
  onFlowEndDateChange,
  hierarchicalCategories = {}, // reserved for future filters / hierarchy UI
  onFlowClick,
  onFlowUpdate // reserved (parent refresh hook)
}) => {
  const { defaultCurrency } = useFinanceProfile();
  const [isFilterExpanded, setIsFilterExpanded] = useState(false);
  const [listHeight, setListHeight] = useState(
    height - HEADER_MAIN_PX - TOTAL_ROW_PX
  );
  const categoryDropdownRef = useRef(null);
  const categL1DropdownRef = useRef(null);
  const categL2DropdownRef = useRef(null);
  const accountDropdownRef = useRef(null);

  useEffect(() => {
    const base = height - HEADER_MAIN_PX - TOTAL_ROW_PX;
    if (isFilterExpanded) {
      setListHeight(base - HEADER_FILTER_ROW_PX);
    } else {
      const timer = setTimeout(() => {
        setListHeight(base);
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [isFilterExpanded, height]);

  const totalStdValue = useMemo(
    () =>
      (flows || []).reduce((sum, f) => sum + Number(f?.std_value ?? 0), 0),
    [flows]
  );

  const totalFormatted = useMemo(
    () =>
      `${formatFlowValueLikeRow(totalStdValue)} ${defaultCurrency || 'BRL'}`,
    [totalStdValue, defaultCurrency]
  );

  const showTotalStrip = !loading && !error;

  const hasActiveFilter =
    Boolean(selectedType) ||
    Boolean(selectedCategory) ||
    (selectedAccount?.trim() ?? '') !== '' ||
    (flowSearchText?.trim() ?? '') !== '' ||
    Boolean(selectedCategL1) ||
    Boolean(selectedCategL2);

  const handleFlowClick = (flow) => {
    if (typeof onFlowClick === 'function') {
      onFlowClick(flow);
    }
  };

  const Row = ({ index, style }) => {
    const flow = flows[index];

    if (!flow) {
      return (
        <div style={style} className="p-3">
          <div className="bg-gray-100 p-3 animate-pulse">
            <div className="h-4 bg-gray-200 rounded mb-2"></div>
            <div className="h-3 bg-gray-200 rounded mb-2"></div>
            <div className="h-3 bg-gray-200 rounded"></div>
          </div>
        </div>
      );
    }

    return (
      <div onClick={() => handleFlowClick(flow)}>
        <DetailedTransactionCard flow={flow} style={style} />
      </div>
    );
  };

  return (
    <div className="bg-white shadow rounded-xl overflow-hidden mt-2">
      <div className="bg-gray-600 border-b border-gray-200 rounded-t-xl">
        <div className="px-4 py-2 flex justify-between items-center gap-2">
          <div className="flex items-center gap-2 text-white min-w-0">
            <h2 className="text-lg font-semibold text-white truncate">Flows</h2>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <div className="relative" ref={categoryDropdownRef}>
              <DropdownCustomDark
                options={categories}
                selectedValue={selectedCategory}
                onSelect={setSelectedCategory}
                placeholder="All"
                width="w-32"
                showAllOption={true}
                allOptionText="All"
                allOptionValue=""
                position="right"
                showRadio={true}
                usePortal={true}
                portalRef={categoryDropdownRef}
              />
            </div>

            <div className="bg-gray-500 bg-opacity-30 rounded-full p-0.5 flex">
              {['In', 'Out'].map((type, index) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => setSelectedType(selectedType === type ? '' : type)}
                  className={`
                    px-3 py-1 text-xs font-medium transition-all
                    ${index === 0 ? 'rounded-l-full' : 'rounded-r-full'}
                    ${selectedType === type
                      ? type === 'In'
                        ? 'text-white bg-green-400'
                        : 'text-white bg-red-400'
                      : 'text-white text-opacity-70 hover:text-opacity-100 hover:bg-gray-500 hover:bg-opacity-20'
                    }
                  `}
                >
                  {type}
                </button>
              ))}
            </div>

            <button
              type="button"
              onClick={() => setIsFilterExpanded((f) => !f)}
              className={`p-1 rounded-full transition-all flex-shrink-0 ${
                hasActiveFilter
                  ? 'bg-green-400 text-white'
                  : 'bg-gray-500 bg-opacity-30 text-white text-opacity-70 hover:bg-opacity-40'
              }`}
              aria-label="Toggle filters"
            >
              <FunnelIcon className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div
          className={`overflow-hidden transition-all duration-200 ease-in-out ${
            isFilterExpanded ? 'max-h-52' : 'max-h-0'
          }`}
        >
          <div className="min-h-10 px-4 py-2 flex flex-wrap items-center bg-gray-500 bg-opacity-20 gap-x-3 gap-y-2">
            {typeof onFlowStartDateChange === 'function' &&
              typeof onFlowEndDateChange === 'function' && (
              <>
                <div
                  className="flex items-center gap-1.5 shrink-0"
                  onClick={(e) => e.stopPropagation()}
                >
                  <span className="text-xs text-white opacity-90 whitespace-nowrap">De</span>
                  <DatePicker
                    name="flowStartDate"
                    value={flowStartDate}
                    onChange={(e) => {
                      const v = e?.target?.value;
                      if (v && typeof v === 'string') onFlowStartDateChange(v);
                    }}
                    mode="single"
                    colorScheme="green"
                    placeholder="Início"
                    className="min-h-[2rem] w-[11rem] shrink-0 border border-gray-300 bg-white shadow-sm"
                  />
                </div>
                <div
                  className="flex items-center gap-1.5 shrink-0"
                  onClick={(e) => e.stopPropagation()}
                >
                  <span className="text-xs text-white opacity-90 whitespace-nowrap">Até</span>
                  <DatePicker
                    name="flowEndDate"
                    value={flowEndDate}
                    onChange={(e) => {
                      const v = e?.target?.value;
                      if (v && typeof v === 'string') onFlowEndDateChange(v);
                    }}
                    mode="single"
                    colorScheme="green"
                    placeholder="Fim"
                    className="min-h-[2rem] w-[11rem] shrink-0 border border-gray-300 bg-white shadow-sm"
                  />
                </div>
              </>
            )}
            {typeof onFlowSearchTextChange === 'function' && (
              <input
                type="search"
                value={flowSearchText}
                onChange={(e) => onFlowSearchTextChange(e.target.value)}
                placeholder="Descrição"
                className="h-6 px-2 rounded text-xs text-gray-800 bg-white border border-gray-300 min-w-[100px] max-w-[160px] placeholder:text-gray-400"
                aria-label="Filtrar por texto na descrição"
              />
            )}
            {typeof setSelectedCategL1 === 'function' && (
              <div className="min-w-[120px]" ref={categL1DropdownRef}>
                <DropdownCustomDark
                  options={categoriesL1}
                  selectedValue={selectedCategL1 || ''}
                  onSelect={setSelectedCategL1}
                  placeholder="All L1"
                  width="min-w-[120px]"
                  showAllOption
                  allOptionText="All L1"
                  allOptionValue=""
                  position="right"
                  showRadio
                  usePortal
                  portalRef={categL1DropdownRef}
                />
              </div>
            )}
            {typeof setSelectedCategL2 === 'function' && (
              <div className="min-w-[120px]" ref={categL2DropdownRef}>
                <DropdownCustomDark
                  options={categoriesL2}
                  selectedValue={selectedCategL2 || ''}
                  onSelect={setSelectedCategL2}
                  placeholder="All L2"
                  width="min-w-[120px]"
                  showAllOption
                  allOptionText="All L2"
                  allOptionValue=""
                  position="right"
                  showRadio
                  usePortal
                  portalRef={categL2DropdownRef}
                />
              </div>
            )}
            {typeof setSelectedAccount === 'function' && (
              <div className="min-w-[130px]" ref={accountDropdownRef}>
                <DropdownCustomDark
                  options={accounts}
                  selectedValue={selectedAccount || ''}
                  onSelect={setSelectedAccount}
                  placeholder="All accounts"
                  width="min-w-[130px]"
                  showAllOption
                  allOptionText="All accounts"
                  allOptionValue=""
                  position="right"
                  showRadio
                  usePortal
                  portalRef={accountDropdownRef}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {showTotalStrip && (
        <div
          className="flex w-full items-center justify-between gap-3 border-t border-gray-200 bg-gray-50 px-4 py-2 text-xs font-medium text-gray-600"
          role="status"
          aria-live="polite"
        >
          <span className="text-left tabular-nums shrink-0">
            Total ({flows.length})
          </span>
          <span className="text-right tabular-nums min-w-0">
            {totalFormatted}
          </span>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <SpinnerLoad />
        </div>
      ) : error ? (
        <div className="flex justify-center items-center h-64">
          <div className="text-center">
            <div className="text-red-500 text-sm">{error}</div>
          </div>
        </div>
      ) : !flows || flows.length === 0 ? (
        <div className="flex justify-center items-center h-64">
          <div className="text-center">
            <div className="text-gray-500 text-sm">No transactions found</div>
          </div>
        </div>
      ) : (
        <List
          height={Math.max(50, listHeight)}
          itemCount={flows.length}
          itemSize={itemSize}
          width="100%"
          overscanCount={5}
        >
          {Row}
        </List>
      )}
    </div>
  );
};

export default DetailedTransactionList;
